import api from "../api/axios";
import { wsManager } from "../websocket/wsManager";
import {
  CHAT_LIST_REQUEST,
  CHAT_LIST_SUCCESS,
  CHAT_LIST_FAIL,
  CHAT_HISTORY_REQUEST,
  CHAT_HISTORY_SUCCESS,
  CHAT_HISTORY_FAIL,
  CHAT_OLDER_MESSAGES_REQUEST,
  CHAT_OLDER_MESSAGES_SUCCESS,
  CHAT_OLDER_MESSAGES_FAIL,
  // MESSAGE_RECEIVED,
  SET_ACTIVE_CHAT,
  // CLEAR_UNREAD,
  CHAT_UPDATE_DATA_REQUEST,
  CHAT_UPDATE_DATA_SUCCESS,
  CHAT_UPDATE_DATA_FAIL,
  CHAT_UPDATE_ROLE_REQUEST,
  CHAT_UPDATE_ROLE_SUCCESS,
  CHAT_UPDATE_ROLE_FAIL,
  CHAT_TRANSFER_OWNERSHIP_REQUEST,
  CHAT_TRANSFER_OWNERSHIP_SUCCESS,
  CHAT_TRANSFER_OWNERSHIP_FAIL,
  CHAT_ADD_MEMBER_REQUEST,
  CHAT_ADD_MEMBER_SUCCESS,
  CHAT_ADD_MEMBER_FAIL,
  CHAT_REMOVE_MEMBER_REQUEST,
  CHAT_REMOVE_MEMBER_SUCCESS,
  CHAT_REMOVE_MEMBER_FAIL,
  CHAT_LEAVE_REQUEST,
  CHAT_LEAVE_SUCCESS,
  CHAT_LEAVE_FAIL,

  CHAT_LIST_ADD,
  CHAT_CREATE_REQUEST,
  CHAT_CREATE_SUCCESS,
  CHAT_CREATE_FAIL,
  CHAT_CREATE_RESET,
} from "../constants/chatConstants";

const CHAT_MESSAGES_PAGE_SIZE = 50;

// ===== CHAT LIST =====
export const loadChats = () => async (dispatch) => {
  try {
    dispatch({ type: CHAT_LIST_REQUEST });

    const { data } = await api.get("/chat/");
    dispatch({ type: CHAT_LIST_SUCCESS, payload: data });

  } catch (err) {
    dispatch({
      type: CHAT_LIST_FAIL,
      payload: err.response?.data || err.message,
    });
  }
};

// ===== CHAT HISTORY =====
export const loadChatHistory = (chatId) => async (dispatch) => {
  try {
    dispatch({
      type: CHAT_HISTORY_REQUEST,
      payload: { chatId },
    });

    const { data } = await api.get(`/chat/${chatId}/messages/`, {
      params: { limit: CHAT_MESSAGES_PAGE_SIZE },
    });

    dispatch({
      type: CHAT_HISTORY_SUCCESS,
      payload: {
        chatId,
        messages: data,
        hasOlder: data.length === CHAT_MESSAGES_PAGE_SIZE,
      },
    });

  } catch (err) {
    dispatch({
      type: CHAT_HISTORY_FAIL,
      payload: err.response?.data || err.message,
    });
  }
};

export const loadOlderMessages = (chatId) => async (dispatch, getState) => {
  const state = getState();
  const messages = state.chat.messagesByChat[chatId] || [];
  const historyState = state.chat.historyByChat?.[chatId] || {};
  const oldestMessage = messages[0];

  if (!oldestMessage?.created_at) return;
  if (historyState.loadingOlder) return;
  if (historyState.hasOlder === false) return;

  try {
    dispatch({
      type: CHAT_OLDER_MESSAGES_REQUEST,
      payload: { chatId },
    });

    const { data } = await api.get(`/chat/${chatId}/messages/`, {
      params: {
        limit: CHAT_MESSAGES_PAGE_SIZE,
        before: oldestMessage.created_at,
      },
    });

    dispatch({
      type: CHAT_OLDER_MESSAGES_SUCCESS,
      payload: {
        chatId,
        messages: data,
        hasOlder: data.length === CHAT_MESSAGES_PAGE_SIZE,
      },
    });

  } catch (err) {
    dispatch({
      type: CHAT_OLDER_MESSAGES_FAIL,
      payload: {
        chatId,
        error: err.response?.data || err.message,
      },
    });
  }
};

// ===== SEND TEXT MESSAGE =====
export const sendTextMessage = (chatId, text) => {
  wsManager.send({
    event: "send_message",
    chat_id: chatId,
    text,
  })
};

// ===== SEND ENTITY SHARE =====
export const sendEntityShare = (chatId, entityType, entityId, requestId) => {
  const payload = {
    event: "send_entity_share",
    chat_id: chatId,
    entity: {
      type: entityType,
      id: entityId,
    },
  };
  if (requestId) {
    payload.request_id = requestId;
  }
  // Do not enqueue for reconnect — picker needs an immediate transport outcome.
  return wsManager.send(payload, { queue: false });
};

// ===== SEND FILE / IMAGE =====
export const uploadMultipleFiles = async (chatId, images = [], files = []) => {
  const formData = new FormData();

  images.forEach((img) => formData.append("images", img));
  files.forEach((file) => formData.append("files", file));

  await api.post(`/chat/${chatId}/upload/`, formData);
};

// ===== SET ACTIVE CHAT
export const setActiveChat = (chatId) => ({
  type: SET_ACTIVE_CHAT,
  payload: chatId,
});

export const setActiveChatNull = () => ({
  type: SET_ACTIVE_CHAT,
  payload: null,
});

// ===== CREATE CHAT =====
export const resetChatCreate = () => ({
  type: CHAT_CREATE_RESET,
});

export const createChat = (
  member_ids,
  name,
  image,
  options = {},
) => async (dispatch) => {
  const publishTransientState = options.publishTransientState !== false;

  try {
    const formData = new FormData();
    member_ids.forEach((member_id) => { formData.append("users", member_id); });
    if (name) formData.append("name", name);
    if (image) formData.append("image", image);

    if (publishTransientState) {
      dispatch({
        type: CHAT_CREATE_REQUEST,
      });
    }

    const { data } = await api.post(
      `/chat/create/`,
      formData,
    );

    const createdChat = data?.chat ?? data;

    if (publishTransientState) {
      dispatch({
        type: CHAT_CREATE_SUCCESS,
        payload: createdChat,
      });
    } else if (createdChat && createdChat.id != null && createdChat.id !== "") {
      dispatch({
        type: CHAT_LIST_ADD,
        payload: createdChat,
      });
    }

    return createdChat;

  } catch (error) {
    if (publishTransientState) {
      dispatch({
        type: CHAT_CREATE_FAIL,
        payload:
          error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message,
      });
    }
    return null;
  }
};

// UPDATE CHAT DATA
export const updateChatData = (chatId, name, image) => async (dispatch, getState) => {
  try {
    const formData = new FormData();
    if (name) formData.append("name", name);
    if (image) formData.append("image", image);

    dispatch({
      type: CHAT_UPDATE_DATA_REQUEST,
    });

    const { data } = await api.post(
      `/chat/${chatId}/update/`,
      formData,
    );

    dispatch({
      type: CHAT_UPDATE_DATA_SUCCESS,
      payload: data,
    });
    return { ok: true, data };

  } catch (error) {
    const errorMessage =
      error.response && error.response.data.detail
        ? error.response.data.detail
        : error.message;
    dispatch({
      type: CHAT_UPDATE_DATA_FAIL,
      payload: errorMessage,
    });
    return { ok: false, error: errorMessage };
  }
};

// UPDATE CHAT ROLE
export const updateChatRole = (chatId, user_id, role) => async (dispatch, getState) => {
  try {

    dispatch({
      type: CHAT_UPDATE_ROLE_REQUEST,
    });

    const { data } = await api.post(
      `/chat/${chatId}/update-role/`,
      {
        user_id: user_id,
        role: role,
      },
    );

    dispatch({
      type: CHAT_UPDATE_ROLE_SUCCESS,
      payload: data,
    });
    return { ok: true, data };

  } catch (error) {
    const errorMessage =
      error.response && error.response.data.detail
        ? error.response.data.detail
        : error.message;
    dispatch({
      type: CHAT_UPDATE_ROLE_FAIL,
      payload: errorMessage,
    });
    return { ok: false, error: errorMessage };
  }
};

// CHAT TRANSFER OWNERSHIP
export const transferChatOwnership = (chatId, user_id) => async (dispatch, getState) => {
  try {

    dispatch({
      type: CHAT_TRANSFER_OWNERSHIP_REQUEST,
    });

    const { data } = await api.post(
      `/chat/${chatId}/transfer-ownership/`,
      {
        user_id: user_id,
      },
    );

    dispatch({
      type: CHAT_TRANSFER_OWNERSHIP_SUCCESS,
      payload: data,
    });
    return { ok: true, data };

  } catch (error) {
    const errorMessage =
      error.response && error.response.data.detail
        ? error.response.data.detail
        : error.message;
    dispatch({
      type: CHAT_TRANSFER_OWNERSHIP_FAIL,
      payload: errorMessage,
    });
    return { ok: false, error: errorMessage };
  }
};

// ADD CHAT MEMBER
export const addChatMember = (chatId, userIds) => async (dispatch, getState) => {
  try {
    dispatch({
      type: CHAT_ADD_MEMBER_REQUEST,
    });

    const formData = new FormData();

    userIds.forEach(id => {
      formData.append("users", id);
    });

    const { data } = await api.post(
      `/chat/${chatId}/add-member/`,
      formData,
    );

    dispatch({
      type: CHAT_ADD_MEMBER_SUCCESS,
      payload: data,
    });
    return { ok: true, data };

  } catch (error) {
    const errorMessage =
      error.response?.data?.detail || error.message;
    dispatch({
      type: CHAT_ADD_MEMBER_FAIL,
      payload: errorMessage,
    });
    return { ok: false, error: errorMessage };
  }
};

// REMOVE CHAT MEMBER
export const removeChatMember = (chatId, memberId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: CHAT_REMOVE_MEMBER_REQUEST,
    });

    const { data } = await api.post(
      `/chat/${chatId}/remove-member/`,
      { user_id: memberId, },
    );

    dispatch({
      type: CHAT_REMOVE_MEMBER_SUCCESS,
      payload: data,
    });
    return { ok: true, data };

  } catch (error) {
    const errorMessage =
      error.response && error.response.data.detail
        ? error.response.data.detail
        : error.message;
    dispatch({
      type: CHAT_REMOVE_MEMBER_FAIL,
      payload: errorMessage,
    });
    return { ok: false, error: errorMessage };
  }
};

// LEAVE CHAT
export const leaveChat = (chatId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: CHAT_LEAVE_REQUEST,
    });

    const { data } = await api.post(
      `/chat/${chatId}/leave/`,
    );

    dispatch({
      type: CHAT_LEAVE_SUCCESS,
      payload: data,
    });
    return { ok: true, data };

  } catch (error) {
    const errorMessage =
      error.response && error.response.data.detail
        ? error.response.data.detail
        : error.message;
    dispatch({
      type: CHAT_LEAVE_FAIL,
      payload: errorMessage,
    });
    return { ok: false, error: errorMessage };
  }
};

// NOTIFICATION-COMPLETION-A1 — per-chat mute (own membership only)
export const setChatNotificationsMuted = (chatId, muted) => async (dispatch, getState) => {
  try {
    const { data } = await api.patch(`/chat/${chatId}/notifications/`, {
      muted: Boolean(muted),
    });
    const existing = (getState().chat?.chats || []).find(
      (chat) => String(chat.id) === String(chatId),
    );
    if (existing) {
      dispatch({
        type: CHAT_UPDATE_DATA_SUCCESS,
        payload: {
          ...existing,
          my_notifications_muted: Boolean(data.muted),
        },
      });
    }
    return { ok: true, muted: Boolean(data.muted), data };
  } catch (error) {
    const responseData = error.response && error.response.data;
    const errorMessage =
      (responseData && typeof responseData.detail === "string" && responseData.detail)
      || (responseData && Array.isArray(responseData.muted) && responseData.muted[0])
      || error.message
      || "Не вдалося оновити сповіщення чату.";
    return { ok: false, error: errorMessage };
  }
};
